import { useMemo } from "react";
import { useMasks } from "@/hooks/useMasks";
import { useProjects } from "@/hooks/useProjects";
import { useSpecLists } from "@/hooks/useSpecLists";
import { useMeasurementSystems } from "@/hooks/useMeasurementSystems";
import { useTestLabels } from "@/hooks/useTestLabels";
import {
  applyMaskFilters,
  applyProjectFilters,
  applySpecListFilters,
  applySystemFilters,
  applyTestLabelFilters,
  groupResultsByCategory,
} from "./filters";

export type { GroupedResults, HoverDetail, SearchResult } from "./filters";

/**
 * Gathers data from multiple endpoints to enable filtering data across the entire application context.
 */
export function useSearchEverywhereData(query: string, maxPerGroup: number) {
  const { data: projects, isLoading: isLoadingProjects } = useProjects();
  const { data: masks, isLoading: isLoadingMasks } = useMasks();
  const { data: specLists, isLoading: isLoadingSpecLists } = useSpecLists();
  const { data: systems, isLoading: isLoadingSystems } = useMeasurementSystems();
  const { data: testLabels, isLoading: isLoadingTestLabels } = useTestLabels();

  const isLoading =
    isLoadingProjects ||
    isLoadingMasks ||
    isLoadingSpecLists ||
    isLoadingSystems ||
    isLoadingTestLabels;

  const q = query.toLowerCase().trim();
  const projectNameById = useMemo(() => new Map(projects.map((p) => [p.id, p.name])), [projects]);

  const results = useMemo(
    () =>
      [
        applyProjectFilters(projects, q, maxPerGroup),
        applyMaskFilters(masks, q, maxPerGroup, projectNameById),
        applySpecListFilters(specLists, q, maxPerGroup),
        applyTestLabelFilters(testLabels, q, maxPerGroup),
        applySystemFilters(systems, q, maxPerGroup),
      ].flat(),
    [q, projects, masks, specLists, systems, testLabels, projectNameById, maxPerGroup],
  );

  const grouped = useMemo(() => groupResultsByCategory(results), [results]);

  return { results, grouped, isLoading };
}
